import "./index.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useContext, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { z } from "zod";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";
import type { ILoginResponse } from "@/api/auth/types";
import { UsersRepository } from "@/api/users/repository";
import { UsersService } from "@/api/users/service";
import { InputControl, InputPasswordControl } from "@/components/controls";
import { useAuthContext } from "@/pages/Auth";
import { ROUTES } from "@/router";
import { Button, Message } from "@/ui-kit";
import { AuthPageContext } from "../../context/AuthPageContext";
import { handleSuccessfulAuthentication } from "../../utils";
import { FormView } from "../FormView";

type IForm = {
  email: string;
  password: string;
};

const SignIn = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const { usernameRef } = useContext(AuthPageContext);

  const { translations } = useAuthContext();

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const userRepository = useMemo(() => {
    return new UsersRepository(new UsersService());
  }, []);

  const schema = useMemo(() => {
    return z.object({
      email: z.string().min(1, { message: t("validation.required") }),
      password: z.string().min(1, { message: t("validation.required") }),
    });
  }, [t]);

  const emailParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("email") || "";
  }, []);

  const defaultValues = useMemo((): IForm => {
    return {
      email: emailParam,
      password: "",
    };
  }, [emailParam]);

  const form = useForm<IForm>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState } = form;

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);
      setMessage("");
      usernameRef.current = formData.email;

      const params = new URLSearchParams(window.location.search);
      const id = params.get("id") || "";
      const token = params.get("token") || "";
      const tenant = params.get("tenant") || "";

      authRepository
        .login({
          username: formData.email,
          password: formData.password,
        })
        .then((response: ILoginResponse) => {
          if (token) {
            return userRepository
              .claimInvite({
                id,
                token,
                email: formData.email,
              })
              .then(() => {
                return response;
              });
          }
          return response;
        })
        .then((response: ILoginResponse) => {
          return handleSuccessfulAuthentication(
            response.accessToken,
            response.refreshToken,
            tenant,
          );
        })
        .catch((e) => {
          console.log(e);
          setMessage(t("validation.invalidEmailOrPassword"));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [authRepository, t, userRepository, usernameRef],
  );

  return (
    <FormProvider {...form}>
      <FormView
        title={translations?.login_welcome_title}
        subtitle={translations?.login_welcome_subtitle}
      >
        <form
          className="nuco-auth__common-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputControl
            name="email"
            size="lg"
            label={t("label.email")}
            placeholder={t("placeholder.enterEmail")}
            invalid={!!formState.errors.email}
            autoFocus={!emailParam}
          />

          <InputPasswordControl
            name="password"
            size="lg"
            label={t("label.password")}
            placeholder={t("placeholder.enterPassword")}
            invalid={!!formState.errors.password}
            autoFocus={!!emailParam}
          />

          {message && (
            <div className="nuco-auth-common-form__message">
              <Message text={message} color="danger" />
            </div>
          )}

          <div className="sign-in-form__extras">
            <Link
              to={`/${ROUTES.AUTH}/${ROUTES.FORGOT_PASSWORD}`}
              className="sign-in-form__forgot-password-button nuco-text nuco-text--md nuco-text--medium"
            >
              {t("label.forgotPassword")}
            </Link>
          </div>

          <div className="nuco-auth__common-form-actions">
            <Button
              type="submit"
              size="lg"
              content={t("action.login")}
              loading={loading}
            />
          </div>
        </form>
      </FormView>
    </FormProvider>
  );
};

export default SignIn;
